import { FileDataDisplay } from "#src/components/DataDisplay/FileDataDisplay";
import { FileUploadedSchema, type FileUploadedType } from "@validereinc/domain";
import { datetimeFormatter } from "@validereinc/utilities";
import classNames from "classnames/bind";
import isValid from "date-fns/isValid";
import parseISO from "date-fns/parseISO";
import React, { type ReactNode } from "react";
import { z } from "zod";
import styles from "./formatUnknownRecordValueForDisplay.module.scss";

const cx = classNames.bind(styles);

export type FormatUnknownRecordValueForDisplayOptions = {
  /** Hide the paperclip icon on uploaded file displays. */
  shouldHideFileIcon?: boolean;
};

/**
 * Renders an uploaded file as a previewable, downloadable filename that matches
 * surrounding typography (e.g. tag content).
 */
const renderUploadedFileForDisplay = (
  file: FileUploadedType,
  shouldHideFileIcon = false
) => (
  <FileDataDisplay
    key={file.ref}
    fileName={file.name}
    fileId={file.ref}
    prefetch={false}
    alignment="left"
    isInline
    shouldInheritTypography
    hideIcon={shouldHideFileIcon}
  />
);

/**
 * Renders one or more uploaded files without comma separators between names.
 */
const renderUploadedFilesForDisplay = (
  files: FileUploadedType[],
  shouldHideFileIcon = false
) => {
  const fileDisplays = files.map((file) =>
    renderUploadedFileForDisplay(file, shouldHideFileIcon)
  );
  if (fileDisplays.length === 1) {
    return fileDisplays[0];
  }

  return <div className={cx("file-list")}>{fileDisplays}</div>;
};

/**
 * Formats JSON-like record values for UI: booleans as Yes/No, valid `Date` instances
 * and ISO date-time strings via {@link datetimeFormatter}, uploaded file objects
 * (`{ ref, name }`) and multi-file arrays via {@link FileDataDisplay}, other arrays
 * as comma-separated recursively formatted items, remaining plain objects as
 * `JSON.stringify`, and remaining primitives as strings. `null` and `undefined`
 * yield an empty string.
 */
export const formatUnknownRecordValueForDisplay = (
  value: unknown,
  options: FormatUnknownRecordValueForDisplayOptions = {}
): ReactNode => {
  const { shouldHideFileIcon = false } = options;
  if (value == null) {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    const multiFileParseResult = z.array(FileUploadedSchema).safeParse(value);
    if (multiFileParseResult.success && multiFileParseResult.data.length > 0) {
      return renderUploadedFilesForDisplay(
        multiFileParseResult.data,
        shouldHideFileIcon
      );
    }
    return value
      .map((item) => formatUnknownRecordValueForDisplay(item, options))
      .join(", ");
  }
  if (value instanceof Date) {
    return isValid(value) ? datetimeFormatter(value) : "";
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = parseISO(value);
    if (isValid(parsed)) {
      return datetimeFormatter(parsed);
    }
  }
  if (typeof value === "object") {
    const fileParseResult = FileUploadedSchema.safeParse(value);
    if (fileParseResult.success) {
      return renderUploadedFileForDisplay(
        fileParseResult.data,
        shouldHideFileIcon
      );
    }
    return JSON.stringify(value);
  }
  return String(value);
};
